import Link from 'next/link';
import { useRouter } from 'next/router';
import { Clock3, Gift, LogOut, Menu, Percent, Settings2, ShoppingCart, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18nSafe } from '../context/I18nContext';
import ConfirmModal from './ConfirmModal';
import HeaderLanguageSelect from './HeaderLanguageSelect';
import CustomNumberInput from './CustomNumberInput';
import Tooltip from './Tooltip';
import { formatTND } from '../lib/currency';
import { getCartItems, removeFromCart, updateCartQuantity } from '../lib/cart';
import { getPendingQuoteRequest, QUOTE_REQUEST_EVENT } from '../lib/quoteRequests';
import { getActivePromo } from '../lib/promo';
import { shouldShowPrices } from '../lib/priceVisibility';
import { localizePath } from '../lib/localizedRoutes';

const NAV_LINKS = [
  { href: '/', key: 'header.nav.home' },
  { href: '/about', key: 'header.nav.about' },
  { href: '/services', key: 'header.nav.services' },
  { href: '/machines/categories', key: 'header.nav.products' },
  { href: '/spare-parts', key: 'header.nav.spareParts' },
  { href: '/contact', key: 'header.nav.contact' },
];

async function fetchJsonOrThrow(url) {
  const response = await fetch(url);
  const data = await response.json().catch(() => []);

  if (!response.ok) {
    throw new Error('Request failed');
  }

  return data;
}

function PromotionDot() {
  return (
    <span
      aria-hidden="true"
      data-testid="promotion-dot"
      className="absolute -right-0.5 -top-0.5 inline-flex h-2.5 w-2.5 items-center justify-center"
    >
      <span className="absolute inset-0 rounded-full bg-[var(--dash-main)] opacity-60 animate-ping" />
      <span className="relative h-2.5 w-2.5 rounded-full bg-[var(--dash-main)] shadow-[0_0_8px_var(--dash-main)]" />
    </span>
  );
}

export default function Header() {
  const router = useRouter();
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { language, languages, setLanguage, t } = useI18nSafe();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [cartMenuOpen, setCartMenuOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const isHomeRoute = router.pathname === '/';
  const accountInitial = (user?.firstName || user?.email || 'U').trim().charAt(0).toUpperCase();
  const cartMenuRef = useRef(null);
  const accountMenuRef = useRef(null);
  const progressFillRef = useRef(null);
  const progressRafRef = useRef(0);
  const progressValueRef = useRef(0);
  const [hasActivePromotions, setHasActivePromotions] = useState(false);
  const [removeCartItemConfirm, setRemoveCartItemConfirm] = useState({ open: false, item: null });
  async function handleLogout() {
    await logout();
    router.push('/auth/login');
  }

  useEffect(() => {
    const scrollSource =
      typeof document !== 'undefined'
        ? document.querySelector('.custom-scrollbar-native')
        : null;

    const onScroll = () => {
      const y = scrollSource ? scrollSource.scrollTop || 0 : window.scrollY;
      setIsScrolled((prev) => {
        if (prev) return y > 24;
        return y > 56;
      });
    };

    onScroll();
    if (scrollSource) {
      scrollSource.addEventListener('scroll', onScroll, { passive: true });
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
    }
    return () => {
      if (scrollSource) {
        scrollSource.removeEventListener('scroll', onScroll);
      } else {
        window.removeEventListener('scroll', onScroll);
      }
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setCartMenuOpen(false);
    setAccountMenuOpen(false);
  }, [router.pathname]);

  useEffect(() => {
    function onPointerDown(event) {
      if (cartMenuRef.current && !cartMenuRef.current.contains(event.target)) {
        setCartMenuOpen(false);
      }
      if (!accountMenuRef.current) return;
      if (accountMenuRef.current.contains(event.target)) return;
      setAccountMenuOpen(false);
    }

    function onEscape(event) {
      if (event.key === 'Escape') {
        setCartMenuOpen(false);
        setAccountMenuOpen(false);
      }
    }

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onEscape);
    };
  }, []);

  useEffect(() => {
    function syncCart() {
      setCartItems(getCartItems());
    }

    syncCart();
    window.addEventListener('storage', syncCart);
    window.addEventListener('cart-updated', syncCart);
    return () => {
      window.removeEventListener('storage', syncCart);
      window.removeEventListener('cart-updated', syncCart);
    };
  }, []);

  useEffect(() => {
    function syncPendingRequest() {
      setPendingRequest(getPendingQuoteRequest());
    }

    syncPendingRequest();
    window.addEventListener('storage', syncPendingRequest);
    window.addEventListener(QUOTE_REQUEST_EVENT, syncPendingRequest);
    return () => {
      window.removeEventListener('storage', syncPendingRequest);
      window.removeEventListener(QUOTE_REQUEST_EVENT, syncPendingRequest);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function syncPromotionPresence() {
      try {
        const [productsData, sparePartsData] = await Promise.all([
          fetchJsonOrThrow(`/api/products?lang=${language}&paginated=false`),
          fetchJsonOrThrow(`/api/spare-parts?lang=${language}&paginated=false`),
        ]);

        if (!isMounted) return;

        const allItems = [
          ...(Array.isArray(productsData) ? productsData : []),
          ...(Array.isArray(sparePartsData) ? sparePartsData : []),
        ];

        setHasActivePromotions(allItems.some((item) => Boolean(getActivePromo(item, language))));
      } catch {
        if (isMounted) {
          setHasActivePromotions(false);
        }
      }
    }

    syncPromotionPresence();

    return () => {
      isMounted = false;
    };
  }, [language]);

  const cartCount = cartItems.reduce((sum, item) => sum + Number(item?.quantity || 1), 0);
  const cartTotal = cartItems.reduce(
    (sum, item) => sum + Number(item?.price || item?.unitPrice || item?.amount || 0) * Number(item?.quantity || 1),
    0
  );
  const hasPendingRequest = Boolean(pendingRequest);
  const cartContinueHref = hasPendingRequest ? '/account/requests' : '/machines/categories';
  const cartContinueLabel = hasPendingRequest
    ? t('header.cart.requestHistory', 'Request History')
    : t('header.cart.continue');
  const showPrices = shouldShowPrices();

  function handleDropdownQuantityChange(itemId, quantity) {
    updateCartQuantity(itemId, Number(quantity || 1));
  }

  function openDropdownRemoveConfirm(item) {
    setCartMenuOpen(false);
    setRemoveCartItemConfirm({ open: true, item });
  }

  function closeDropdownRemoveConfirm() {
    setRemoveCartItemConfirm({ open: false, item: null });
  }

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('no-theme-transition');
    root.classList.add('dark');
    localStorage.setItem('app-theme', 'dark');

    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => root.classList.remove('no-theme-transition'));
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      root.classList.remove('no-theme-transition');
    };
  }, []);

  useEffect(() => {
    function applyProgress(nextValue) {
      const next = Math.max(0, Math.min(100, Number(nextValue) || 0));
      progressValueRef.current = next;
      if (!progressFillRef.current) return;
      progressFillRef.current.style.transform = `scaleX(${next / 100})`;
    }

    function onHeroAutoplayProgress(event) {
      const next = Number(event?.detail?.progress);
      if (!Number.isFinite(next)) return;
      cancelAnimationFrame(progressRafRef.current);
      progressRafRef.current = requestAnimationFrame(() => applyProgress(next));
    }

    window.addEventListener('hero-autoplay-progress', onHeroAutoplayProgress);
    applyProgress(progressValueRef.current);
    return () => {
      cancelAnimationFrame(progressRafRef.current);
      window.removeEventListener('hero-autoplay-progress', onHeroAutoplayProgress);
    };
  }, []);

  useEffect(() => {
    if (!isHomeRoute) {
      progressValueRef.current = 0;
      if (progressFillRef.current) {
        progressFillRef.current.style.transform = 'scaleX(0)';
      }
    }
  }, [isHomeRoute]);

  return (
    <>
      <div className="sticky top-0 z-50">
        <header
          className={`relative z-10 transition-all duration-300 ${
          'bg-[linear-gradient(45deg,rgba(0,0,0,1)_30%,rgba(18,18,24,1)_70%)] backdrop-blur-md'
        } ${
          isScrolled ? 'py-5' : 'py-7'
        }`}
        >
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-10 xl:gap-20">
              <Link href="/" className="group inline-flex shrink-0 items-center gap-3">

                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/logos/logo.svg"
                    alt="ETC Company"
                    width="468"
                    height="212"
                    className={`h-auto w-[120px] min-w-[120px] shrink-0 origin-left transition-transform duration-300 ${
                      isScrolled ? 'scale-[0.75]' : 'scale-100'
                    }`}
                  />
            
              </Link>

              <nav className="hidden min-w-0 items-center gap-8 xl:flex">
                {NAV_LINKS.map((item) => {
                  const localizedHref = localizePath(item.href, language);
                  const isProductsNav = item.href === '/machines/categories';
                  const isSparePartsNav = item.href === '/spare-parts';
                  const isActive = isProductsNav
                    ? router.pathname === '/machines' ||
                      router.pathname === '/machines/categories' ||
                      router.pathname === '/machines/[id]'
                    : isSparePartsNav
                    ? router.pathname === '/spare-parts' || router.pathname === '/spare-parts/[id]'
                    : router.pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      href={localizedHref}
                      className="group relative inline-flex flex-col items-center"
                    >
                      <div className="relative h-7 overflow-hidden text-md">
                        <div
                          className={`transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] ${
                            isActive ? '-translate-y-1/2' : 'translate-y-0 group-hover:-translate-y-1/2'
                          }`}
                        >
                          <div
                            className={`h-7 leading-7 whitespace-nowrap ${
                              isActive ? 'text-white' : 'text-[var(--dash-text-soft)] group-hover:text-white'
                            }`}
                          >
                            {t(item.key)}
                          </div>
                          <div className="h-7 leading-7 whitespace-nowrap font-medium text-[var(--dash-main)]">
                            {t(item.key)}
                          </div>
                        </div>
                      </div>
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-l-[4px] border-r-[4px] border-t-[4px] border-l-transparent border-r-transparent border-t-[var(--dash-other)] transition-all duration-300 rounded-lg ${
                          isActive ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100'
                        }`}
                      />
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="hidden shrink-0 items-center gap-3 xl:flex">
              <div className="inline-flex items-center gap-2 text-xs text-[var(--dash-text-muted)]">
                <HeaderLanguageSelect
                  value={language}
                  onChange={setLanguage}
                  options={languages.map((lang) => ({ value: lang, label: lang.toUpperCase() }))}
                  className="w-[92px]"
                  triggerClassName="h-9"
                  panelClassName="min-w-[92px]"
                />
              </div>
              {isLoading ? <span className="px-2 text-xs text-[var(--dash-text-muted)]">{t('header.session')}</span> : null}
              {!isLoading ? (
                <Tooltip content={t('titlebar.routes.promotions')} wrapperClassName="shrink-0">
                  <Link
                    href={localizePath('/promotions', language)}
                    className="relative inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--dash-border-strong)] bg-[var(--dash-surface)] text-[var(--dash-text-soft)] transition-colors hover:bg-[var(--dash-surface-2)]"
                    aria-label={t('titlebar.routes.promotions')}
                  >
                    <Percent className="h-4 w-4" />
                    {hasActivePromotions ? <PromotionDot /> : null}
                  </Link>
                </Tooltip>
              ) : null}
              {!isLoading && isAuthenticated ? (
                <>
                  <div className="relative" ref={cartMenuRef}>
                    <Tooltip content={t('titlebar.routes.cart')} wrapperClassName="shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setAccountMenuOpen(false);
                          setCartMenuOpen((prev) => !prev);
                        }}
                        aria-expanded={cartMenuOpen}
                        className="relative inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--dash-border-strong)] bg-[var(--dash-surface)] text-[var(--dash-text-soft)] transition-colors hover:bg-[var(--dash-surface-2)]"
                        aria-label={t('titlebar.routes.cart')}
                      >
                        <ShoppingCart className="h-4 w-4" />
                        {cartCount > 0 ? (
                          <span className="absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[var(--dash-main)] px-1 text-[10px] font-bold leading-none text-white">
                            {cartCount > 99 ? '99+' : cartCount}
                          </span>
                        ) : null}
                      </button>
                    </Tooltip>

                    <div
                      className={`absolute right-0 top-full z-30 mt-3 w-80 overflow-hidden rounded-xl border border-[var(--dash-border)] bg-[var(--dash-surface)] shadow-[0_20px_50px_rgba(0,0,0,0.35)] transition-[opacity,transform] duration-200 ${
                        cartMenuOpen
                          ? 'visible scale-100 opacity-100 pointer-events-auto'
                          : 'invisible scale-95 opacity-0 pointer-events-none'
                      }`}
                    >
                      <div className="bg-[var(--dash-black)] border-b border-[var(--dash-border)] px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--dash-text-muted)]">
                          {t('header.cart.title')}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-[var(--dash-text)]">
                          {cartCount} {cartCount === 1 ? t('header.cart.item') : t('header.cart.items')}
                        </p>
                      </div>

                      {cartItems.length > 0 ? (
                        <div className="max-h-72 overflow-y-auto">
                          {cartItems.map((item, index) => (
                            <div
                            key={item?.cartKey || `${item?.id || item?._id || item?.slug || item?.name || 'item'}-${index}`}
                              className="group relative overflow-hidden bg-[var(--dash-black)] border-b border-[var(--dash-border)] last:border-b-0"
                            >
                              <div className="px-4 py-3 pr-10 transition-all duration-300 ease-out group-hover:-translate-x-1">
                                <p className="line-clamp-2 text-sm font-medium text-[var(--dash-text)]">
                                  {item?.name || item?.title || 'Machine'}
                                </p>
                                <div className="mt-2 flex items-center justify-between gap-3 text-xs">
                                  <label className="inline-flex items-center gap-2 text-[var(--dash-text-muted)]">
                                    {t('header.cart.qty')}
                                    <CustomNumberInput
                                      value={Number(item?.quantity || 1)}
                                      onChange={(next) =>
                                        handleDropdownQuantityChange(
                                          item?.cartKey || item?.id || item?._id,
                                          Math.max(1, Number(next || 1)),
                                        )
                                      }
                                      min={1}
                                      step={1}
                                      className="w-16"
                                      inputClassName="w-full rounded-lg border border-[var(--dash-border-strong)] bg-[var(--dash-surface)] px-3 py-3 text-xs text-[var(--dash-text)]"
                                    />
                                  </label>
                                  <span className="font-semibold text-[var(--dash-text-soft)]">
                                    {showPrices
                                      ? formatTND(
                                          Number(item?.price || item?.unitPrice || item?.amount || 0) *
                                            Number(item?.quantity || 1)
                                        )
                                      : t('common.priceOnRequest', 'Price on request')}
                                  </span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => openDropdownRemoveConfirm(item)}
                                className="absolute right-0 top-0 inline-flex h-full w-8 translate-x-full items-center justify-center bg-[var(--dash-main)] text-white opacity-0 transition-all duration-300 ease-out group-hover:translate-x-0 group-hover:opacity-100"
                                aria-label={`${t('cartPage.removeItem')} ${item?.name || t('header.cart.item')}`}
                              >
                                <Tooltip content={t('cartPage.removeItem')}>
                                  <X className="h-3.5 w-3.5" />
                                </Tooltip>
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="px-4 py-8 text-center">
                          <p className="text-sm font-medium text-[var(--dash-text)]">{t('header.cart.emptyTitle')}</p>
                          <p className="mt-1 text-xs text-[var(--dash-text-muted)]">
                            {t('header.cart.emptyText')}
                          </p>
                        </div>
                      )}

                      <div className="border-t border-[var(--dash-border)] bg-[var(--dash-black)] px-4 py-3">
                        <div className="mb-3 flex items-center justify-between">
                          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--dash-text-muted)]">
                            {t('header.cart.total')}
                          </span>
                          <span className="text-sm font-bold text-[var(--dash-text)]">
                            {showPrices ? formatTND(cartTotal) : t('common.priceOnRequest', 'Price on request')}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Link
                            href="/cart"
                            onClick={() => setCartMenuOpen(false)}
                            className="flex-1 rounded-lg border border-[var(--dash-border-strong)] px-3 py-2 text-center text-xs font-semibold text-[var(--dash-text-soft)] hover:bg-[var(--dash-surface)]"
                          >
                            {t('header.cart.viewCart')}
                          </Link>
                          <Link
                            href={cartContinueHref}
                            onClick={() => setCartMenuOpen(false)}
                            className="flex-1 rounded-lg bg-[var(--dash-main)] px-3 py-2 text-center text-xs font-semibold text-white transition-colors hover:bg-[var(--dash-other)] hover:text-[var(--dash-black)]"
                          >
                            {cartContinueLabel}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="relative leading-none" ref={accountMenuRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setCartMenuOpen(false);
                        setAccountMenuOpen((prev) => !prev);
                      }}
                      aria-expanded={accountMenuOpen}
                      className="inline-flex items-center justify-center bg-transparent p-0 text-left"
                    >
                      {user?.avatar ? (
                        <>
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={user.avatar}
                            alt="User avatar"
                            className="h-8 w-8 rounded-full border border-[var(--dash-border-strong)] object-cover leading-none"
                          />
                        </>
                      ) : (
                        <div
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-[var(--dash-border-strong)] bg-[var(--dash-primary)] text-xs font-semibold text-white"
                        >
                          {accountInitial}
                        </div>
                      )}
                    </button>
                    <div
                      className={`absolute right-0 top-full z-30 mt-3 min-w-[220px] overflow-hidden rounded-lg border border-[var(--dash-border)] bg-[var(--dash-surface)] shadow-lg transition-[opacity,transform] duration-200 ${
                        accountMenuOpen
                          ? 'visible scale-100 opacity-100 pointer-events-auto'
                          : 'invisible scale-95 opacity-0 pointer-events-none'
                      }`}
                    >
                      <Link
                        href="/account"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--dash-text-soft)] hover:bg-[var(--dash-surface-2)]"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        <Settings2 className="h-4 w-4" />
                        {t('header.account.profileSettings')}
                      </Link>
                      <Link
                        href="/account/requests"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--dash-text-soft)] hover:bg-[var(--dash-surface-2)]"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        <Clock3 className="h-4 w-4" />
                        {t('header.account.requestHistory')}
                      </Link>
                      <button
                        type="button"
                        onClick={async () => {
                          setAccountMenuOpen(false);
                          await handleLogout();
                        }}
                        className="block w-full px-3 py-2 text-left text-sm text-[var(--dash-danger)] hover:bg-[var(--dash-danger-bg)]"
                      >
                        {t('header.account.logout')}
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
              {!isLoading && !isAuthenticated ? (
                <>
                  <Link href="/auth/login" className="rounded-md px-2.5 py-1.5 text-xs font-medium text-[var(--dash-text-soft)] transition-all duration-150 hover:bg-[var(--dash-surface-2)]">
                    {t('header.auth.login')}
                  </Link>
                  <Link
                    href="/auth/signup"
                    className="rounded-md bg-[#ff5d10] px-2.5 py-1.5 text-xs font-semibold text-white transition-all duration-150 hover:bg-[#e14f08]"
                  >
                    {t('header.auth.signup')}
                  </Link>
                </>
              ) : null}
            </div>

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#ff5d10] text-white xl:hidden"
              aria-label={t('header.openMenu')}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
        </header>
        {isHomeRoute ? (
          <div className="pointer-events-none absolute inset-x-0 top-full leading-[0]">
            <div className="absolute -top-[2px] left-0 right-0 z-0 flex justify-center">
              <div className="relative h-1 w-full overflow-hidden bg-white/20">
                <div
                  ref={progressFillRef}
                  className="absolute left-0 top-0 h-full w-full origin-left scale-x-0 transform-gpu bg-[var(--dash-main)] will-change-transform"
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div
        className={`fixed inset-0 z-[60] bg-[var(--dash-surface)] transition-all duration-300 xl:hidden ${
          isMobileMenuOpen
            ? 'translate-x-0 opacity-100 pointer-events-auto visible'
            : 'translate-x-full opacity-0 pointer-events-none invisible'
        }`}
        aria-hidden={!isMobileMenuOpen}
      >
        <div className="container mx-auto h-full px-6 py-6">
          <div className="mb-8 flex items-center justify-between">
            <span className="text-lg font-black tracking-tight text-[var(--dash-text)]">{t('header.menu')}</span>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(false)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[var(--dash-border-strong)] text-[var(--dash-text-soft)]"
              aria-label={t('header.closeMenu')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="flex flex-col gap-2">
            {NAV_LINKS.map((item) => {
              const localizedHref = localizePath(item.href, language);
              const isProductsNav = item.href === '/machines/categories';
              const isSparePartsNav = item.href === '/spare-parts';
              const isActive = isProductsNav
                ? router.pathname === '/machines' ||
                  router.pathname === '/machines/categories' ||
                  router.pathname === '/machines/[id]'
                : isSparePartsNav
                ? router.pathname === '/spare-parts' || router.pathname === '/spare-parts/[id]'
                : router.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={localizedHref}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`rounded-lg px-4 py-3 text-base font-medium transition ${
                    isActive
                      ? 'bg-[var(--dash-primary)] text-white'
                      : 'text-[var(--dash-text-soft)] hover:bg-[var(--dash-surface-2)]'
                  }`}
                >
                  {t(item.key)}
                </Link>
              );
            })}
          </nav>

          <div className="mt-8 flex flex-col gap-2">
            <div className="inline-flex items-center gap-2 rounded-lg px-1 py-1 text-xs text-[var(--dash-text-muted)]">
              {/* <span>{t('common.language')}</span> */}
              <HeaderLanguageSelect
                value={language}
                onChange={setLanguage}
                options={languages.map((lang) => ({ value: lang, label: lang.toUpperCase() }))}
                className="w-[96px]"
                triggerClassName="h-9 border-[var(--dash-border-strong)] bg-[var(--dash-surface-2)] text-[var(--dash-text)] hover:bg-[var(--dash-surface)]"
                panelClassName="min-w-[96px]"
              />
            </div>
              {isLoading ? <span className="mt-1 px-1 text-xs text-[var(--dash-text-muted)]">{t('header.session')}</span> : null}
            {!isLoading ? (
              <Link
                href={localizePath('/promotions', language)}
                className="relative inline-flex items-center gap-2 rounded-lg border border-[var(--dash-border-strong)] px-4 py-3 text-sm font-medium text-[var(--dash-text-soft)] transition-all duration-150"
              >
                <Gift className="h-4 w-4" />
                {hasActivePromotions ? <PromotionDot /> : null}
                {t('titlebar.routes.promotions')}
              </Link>
            ) : null}
            {!isLoading && isAuthenticated ? (
              <>
                <Link
                  href="/cart"
                  className="inline-flex items-center gap-2 rounded-lg border border-[var(--dash-border-strong)] px-4 py-3 text-sm font-medium text-[var(--dash-text-soft)]"
                >
                  <ShoppingCart className="h-4 w-4" />
                  {t('titlebar.routes.cart')}
                </Link>
                <Link
                  href="/account"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex items-center gap-2 rounded-lg border border-[var(--dash-border-strong)] px-4 py-3 text-sm font-medium text-[var(--dash-text-soft)]"
                >
                  <Settings2 className="h-4 w-4" />
                  <span className="flex items-center gap-3">
                    {user?.avatar ? (
                      <>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={user.avatar}
                          alt="User avatar"
                          className="h-10 w-10 rounded-full border border-[var(--dash-border-strong)] object-cover"
                        />
                      </>
                    ) : (
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border-2 border-[var(--dash-border-strong)] bg-[var(--dash-primary)] text-sm font-semibold text-white">
                        {accountInitial}
                      </span>
                    )}
                  </span>
                  {t('header.account.profileSettings')}
                </Link>
                <Link
                  href="/account/requests"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex items-center gap-2 rounded-lg border border-[var(--dash-border-strong)] px-4 py-3 text-sm font-medium text-[var(--dash-text-soft)]"
                >
                  <Clock3 className="h-4 w-4" />
                  {t('header.account.requestHistory')}
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center gap-2 rounded-lg border border-[var(--dash-border-strong)] px-4 py-3 text-sm font-medium text-[var(--dash-danger)]"
                >
                  <LogOut className="h-5 w-5" />
                  {t('header.account.logout')}
                </button>
              </>
            ) : null}
            {!isLoading && !isAuthenticated ? (
              <>
                <Link href="/auth/login" className="rounded-lg border border-[var(--dash-border-strong)] px-4 py-3 text-sm font-medium text-[var(--dash-text-soft)] transition-all duration-150">
                  {t('header.auth.login')}
                </Link>
                <Link
                  href="/auth/signup"
                  className="rounded-lg bg-[#ff5d10] px-4 py-3 text-sm font-semibold text-white transition-all duration-150 hover:bg-[#e14f08]"
                >
                  {t('header.auth.signup')}
                </Link>
              </>
            ) : null}
          </div>
        </div>
      </div>
      <ConfirmModal
        open={removeCartItemConfirm.open}
          title={t('cartPage.removeItemConfirmTitle')}
          description={
            removeCartItemConfirm.item?.name
              ? t('cartPage.removeItemDescriptionNamed', { name: removeCartItemConfirm.item.name })
              : t('cartPage.removeItemDescriptionGeneric')
          }
        confirmLabel={t('cartPage.removeItem')}
        danger
        onCancel={closeDropdownRemoveConfirm}
        onConfirm={() => {
          const target = removeCartItemConfirm.item;
          if (target) {
            removeFromCart(target.cartKey || target.id || target._id);
          }
          closeDropdownRemoveConfirm();
        }}
      />
    </>
  );
}
